import { useQuery } from '@apollo/client';
import {
  AccessControl,
  ALL_MODULES,
  AppContext,
  AsyncCheckboxV2,
  AsyncRadioV2,
  AsyncSelectV2,
  COMMON_CONSTANTS,
  CustomDateField,
  CustomTextField,
  EventEmitter,
  getParsedCrudData,
  getTranslatedText,
  InlineEdit,
  InlineEditTableWrapper,
  isAuthorised,
  SecureText,
  SkeletonLoading,
  useIsAuthorisedHook,
  useProduct,
  useUserMSPCheckHook,
  withErrorBoundary,
  SuperSelect,
  DisabledTag,
  convertToMSPTimeZone
} from '@nile/components';
import InlineDependantSelect from '@nile/components/src/Components/InlineEdit/Components/InlineDependantSelect';
import InlineSelect from '@nile/components/src/Components/InlineEdit/Components/InlineSelect';
import {
  AccountClient,
  FETCH_CLIENT_CONTRACTS_LIST_FOR_TICKET,
  GET_DELETED_CUSTOM_FIELDS,
  GET_ENTITY_NAMES_META,
  GET_TICKET_FORM,
  GET_DELETED_FIELDS,
  TicketClient,
  GET_INACTIVE_FIELDS_FOR_TICKET
} from '@nile/graphql';
import {
  AsyncMultiSelectUser,
  DateTimeCell,
  DependantCell,
  DualCell,
  HTMLCell,
  LinkCell,
  MultiSelectTextCell,
  NameCell,
  PriorityCell,
  StepCell,
  TagCell,
  TextCell,
  TimerCell
} from '@nile/mangifera/src/Components/Ticket';
import InlineStatusCell from '@nile/mangifera/src/Components/Ticket/InlineStatusCell';
import {
  PROPERTY_ORDER,
  REQUIRED_TICKET_FIELDS
} from '@nile/mangifera/src/Constants/Tickets';
import {
  getAccessor,
  getCellType,
  getDependantField,
  getParentField,
  isDependantField,
  isEditable
} from '@nile/mangifera/src/Pebbles/TicketContainer';
import { SVGIcon } from '@superops/super-icons';
import { Heading, TextStyle, TextField } from '@superops/ui-design-system';
import { cloneDeep, includes, isEmpty, sortBy } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TicketSLA from '@nile/mangifera/src/Pages/TicketDetails/Components/TicketSLA.js';
import { Tooltip } from 'react-clone-tippy';
import {
  Wrap,
  WrapperTag
} from '@nile/components/src/Components/InlineEdit/Components/InlineSelect';
import { useCheckIsItProduct } from '@nile/components/src/Utils/helpers';
import DependentFieldCell from '@nile/components/src/CustomFields/FieldsTable/DependantFieldCell';
import { getCellValue } from '@nile/reports/src/modules/EfficiencyReport/util';

const FORMSTATES = {
  BLOCK: 'block',
  DRAW: 'draw'
};

const getFormFields = (data, meta) => {
  const customFieldsMeta = [];
  const ticketFieldsMeta = [];
  meta.forEach((el) => {
    if (el?.isCustom) {
      customFieldsMeta.push(el);
    } else {
      ticketFieldsMeta.push(el);
    }
  });

  const fieldDetails = data?.fieldDetails
    ? JSON.parse(data?.fieldDetails)
    : null;

  const templateFields = fieldDetails?.layout;
  const customFields = [];
  const ticketFields = [];

  if (templateFields) {
    templateFields?.forEach((fieldData) => {
      const fieldType = fieldData?.field?.type;
      const fieldProps = fieldData?.field?.fieldProps;
      if (fieldType === 'custom' && fieldProps) {
        const customMetaData = customFieldsMeta.find(
          (customMeta) => customMeta?.name === fieldProps?.name
        );
        if (customMetaData) {
          customFields.push(customMetaData);
        }
      } else if (fieldType !== 'custom' && fieldProps) {
        const fieldMetaData = ticketFieldsMeta.find(
          (fieldMeta) => fieldMeta?.name === fieldProps?.name
        );
        ticketFields.push(fieldMetaData);
      }
    });
  }

  const allFields = [...ticketFields, ...customFields];

  return {
    ticketFields: !isEmpty(ticketFields) ? ticketFields : null,
    customFields: !isEmpty(customFields) ? customFields : null,
    allFields: !isEmpty(allFields) ? allFields : null
  };
};

const FieldComponentWrapper = (props) => {
  const {
    label,
    children,
    flag,
    classNames,
    fullWidth,
    dependantField,
    responseEntityFields
  } = props;
  const newLayoutFlag = flag;
  const intl = useIntl();
  const classes = [
    'ticket-form-field-wrapper',
    classNames,
    fullWidth ? 'full-width' : 'w100'
  ]
    .filter(Boolean)
    .join(' ');

  const parentField =
    (dependantField &&
      responseEntityFields?.getEntitiesMeta?.[0]?.attributes?.find(
        (el) => el?.name === dependantField?.nestedTo
      )) ||
    null;

  if (!newLayoutFlag) {
    return (
      <Field>
        <div className="flex col full-height w100">
          <Label>{label || ''}</Label>
          {children}
        </div>
      </Field>
    );
  } else {
    return (
      <div className={classes}>
        <div className="flex g5">
          <TextStyle variation="subdued">{label || ''}</TextStyle>
          {dependantField?.isDependent && (
            <DependentFieldCell
              cell={{
                value: parentField?.isCustom
                  ? parentField?.label
                  : getTranslatedText(intl, parentField?.label) || null,
                iconSize: 'sm'
              }}
            />
          )}
        </div>
        {children}
      </div>
    );
  }
};

const Field = styled.div`
  position: relative;
  width: 50%;
  min-height: 68px;
  flex-grow: 1;
  padding-right: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

const FieldWrapper = styled.div`
  width: 100%;
  .value_selector__control {
    border: none;
    border-bottom: 1px solid #ececec;
  }
  .value_selector__single-value {
    font-weight: bold;
  }
`;

const Label = styled.div`
  color: var(--sops-label-text);
  text-align: left;
  margin-left: 10px;
  margin-top: 10px;
`;

const CELL_TYPES = {
  Priority: PriorityCell,
  Link: LinkCell,
  HTML: HTMLCell,
  Text: TextCell,
  Timer: TimerCell,
  Status: TagCell,
  Step: StepCell,
  Name: NameCell,
  DateTime: DateTimeCell,
  Dependant: DependantCell,
  Dual: DualCell,
  MultiSelectText: MultiSelectTextCell,
  AsyncMultiSelectUser: AsyncMultiSelectUser
};

function EditableField(props) {
  const { field, ticket } = props;
  return (
    <FieldWrapper className="displayFields">
      <Label>{field.label || ''}</Label>
      <div className="ml-12">{ticket[field.name] || '--'}</div>
    </FieldWrapper>
  );
}

const getDefaultValue = (fieldValue, fieldName) => {
  switch (fieldName) {
    case 'techGroup':
    case 'sop':
      return fieldValue
        ? {
            ...fieldValue,
            name: fieldValue.name,
            label: fieldValue.name,
            value: fieldValue.name
          }
        : '';
    case 'technician':
      return fieldValue
        ? {
            ...fieldValue,
            name: `${fieldValue.firstName} ${fieldValue.lastName}`,
            label: `${fieldValue.firstName} ${fieldValue.lastName}`,
            value: `${fieldValue.firstName} ${fieldValue.lastName}`
          }
        : '';
    case 'followers':
      return fieldValue?.length
        ? fieldValue.map((el) => {
            return {
              ...el,
              name: el.name,
              label: el.name,
              value: el.name
            };
          })
        : [];
    case 'additionalRequester':
      return fieldValue?.length
        ? fieldValue.map((el) => {
            return {
              ...el,
              name: el.name,
              label: el.name,
              value: el.name
            };
          })
        : [];
    case 'site':
      return fieldValue
        ? {
            name: fieldValue.name,
            label: fieldValue.name,
            value: fieldValue.name,
            id: fieldValue.id
          }
        : '';
    default:
      return fieldValue
        ? { name: fieldValue, label: fieldValue, value: fieldValue }
        : '';
  }
};

function TicketDetailForm(props) {
  const { intl, ticket, noCustomFields, creationForm, showProgress } = props;
  const isMSP = useUserMSPCheckHook();
  const flags = useFlags();
  const dependantFieldFlag = flags?.dependentField;

  const isFormAuthorised = isMSP
    ? useIsAuthorisedHook('ticketForm.view')
    : false;

  const isContractEditauthorised = useIsAuthorisedHook(
    'ticketContractField.edit'
  );

  const { data: responseEntityFields, loading, error } = useQuery(
    GET_ENTITY_NAMES_META,
    {
      client: AccountClient,
      variables: {
        entitiesName: ['Ticket']
      }
    }
  );

  const { loading: formLoading, data: formData, error: formError } = useQuery(
    GET_TICKET_FORM,
    {
      fetchPolicy: 'network-only',
      skip: !ticket?.form?.formId || !isFormAuthorised,
      variables: {
        ticketFormId: ticket?.form?.formId
      }
    }
  );

  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const userRole = currentUser?.role?.roleFeatureList || [];
  const isEditAuthorised = isAuthorised(userRole, 'tickets.edit');
  const isCustomFieldAuthorised = isAuthorised(userRole, 'customFields.view');
  const [searchString, setSearchString] = useState('');
  const [filteredTicketFields, setFilteredTicketFields] = useState(null);
  const [filteredTechnicianFields, setFilteredTechnicianFields] = useState(
    null
  );
  const [filterdCustomFields, setFilteredCustomFields] = useState(null);
  const [filteredClientFields, setFilteredClientFields] = useState(null);
  const [filteredFormTicketFields, setFilteredFormTicketFields] = useState(
    null
  );
  const [filteredFormCustomFields, setFilteredFormCustomFields] = useState(
    null
  );
  const isTransitioning = React.useRef(false);

  const getFielteredItems = (arr, str) => {
    if (!Array.isArray(arr) || typeof str !== 'string') {
      return null; // Input validation: array must be an array and str must be a string
    }

    const search = str.toLowerCase();
    let result = [];

    arr.forEach((field) => {
      let fieldName = field?.name ? field.name.toLowerCase() : null;
      let fieldLabel = field?.label ? field.label.toLowerCase() : null;

      if (fieldLabel && fieldLabel.includes(search)) {
        result.push(field);
      } else if (fieldName && fieldName.includes(search)) {
        result.push(field);
      }
    });

    return result; // Return null if no matches found, otherwise return the filtered array
  };

  const onSearch = (searchString) => {
    const str = searchString.trim();
    setSearchString(searchString);
    if (!str) {
      setFilteredTicketFields(null);
      setFilteredTechnicianFields(null);
      setFilteredCustomFields(null);
      setFilteredClientFields(null);
      setFilteredFormTicketFields(null);
      setFilteredFormCustomFields(null);
    } else {
      const ticketFields = getFielteredItems(allTicketFields, str);
      const techFields = getFielteredItems(allTechnicianFields, str);
      const custFields = getFielteredItems(customFields, str);
      const clientFields = getFielteredItems(allClientFields, str);

      const formTicketFields =
        isFormAuthorised && getFielteredItems(ticketFieldsFromForm, str);
      const formClientFields =
        isFormAuthorised && getFielteredItems(customFieldsFromForm, str);

      setFilteredTicketFields(ticketFields);
      setFilteredTechnicianFields(techFields);
      setFilteredCustomFields(custFields);
      setFilteredClientFields(clientFields);
      setFilteredFormTicketFields(formTicketFields);
      setFilteredFormCustomFields(formClientFields);
    }
  };

  const inactiveCustomFieldsQuery = GET_INACTIVE_FIELDS_FOR_TICKET;

  const inactiveCustomFieldsClient = AccountClient;
  const inactiveCustomFieldsVariables = {
    input: ALL_MODULES.ticket
  };

  const {
    data: inactiveCustomFieldsData,
    loading: inactiveCustomFieldsLoading
  } = useQuery(inactiveCustomFieldsQuery, {
    client: inactiveCustomFieldsClient,
    variables: inactiveCustomFieldsVariables,
    skip: !isCustomFieldAuthorised
  });
  if (loading || inactiveCustomFieldsLoading || formLoading)
    return (
      <SkeletonLoading
        hideHeader
        hideSecondaryHeader
        hideStepNav
        hideTable
        showPropertyCard
        showPlainCardView={false}
        showCardView={false}
        propertyCardFullWidth
      />
    );
  if (error) return <div>Error...</div>;
  const entityFields = cloneDeep(responseEntityFields);
  entityFields.getEntitiesMeta = entityFields.getEntitiesMeta.map(
    (metaEntity) => {
      return {
        ...metaEntity,
        attributes: metaEntity.attributes.map((field) => {
          return {
            ...field,
            label: getTranslatedText(intl, field.label || field.name)
          };
        })
      };
    }
  );
  const allEntityFields =
    entityFields.getEntitiesMeta.find((el) => el.name === 'Ticket')
      ?.attributes || [];
  const propertyOrder = props.propertyOrder || PROPERTY_ORDER;
  const formFields = isFormAuthorised
    ? getFormFields(formData?.getTicketForm, allEntityFields)
    : null;
  let customFieldsFromForm = formFields?.customFields;
  let ticketFieldsFromForm = formFields?.ticketFields;
  // Change the approach to map to avoid multiple loops for ordering
  const allFields = allEntityFields.filter((el) => {
    const isFieldPresentInForm = ticketFieldsFromForm?.find(
      (field) => field?.name === el?.name
    );
    return !el.isCustom && !isFieldPresentInForm;
  });

  let customFields = allEntityFields.filter((el) => {
    const isCustomFieldPresentInForm = customFieldsFromForm?.find(
      (field) => field?.name === el?.name
    );
    return el.isCustom && !isCustomFieldPresentInForm;
  });
  /**
   * We are removing the deleted custom fields.
   * But the deleted field which has value will be preserved.
   */
  const inactiveCustomFields =
    inactiveCustomFieldsData?.getInactiveFields || [];
  customFields = sortBy(
    customFields.filter((el) => {
      return (
        !inactiveCustomFields.some((cf) => cf.columnName === el.name) ||
        (inactiveCustomFields.some((cf) => cf.columnName === el.name) &&
          ticket?.customFields?.[el.name])
      );
    }),
    (el) => el?.label.toLowerCase()
  );
  // Remove newly deleted fields from the custom fields for form
  customFieldsFromForm = !isEmpty(customFieldsFromForm)
    ? customFieldsFromForm.filter((el) => {
        return (
          !inactiveCustomFields.some((cf) => cf.columnName === el.name) ||
          (inactiveCustomFields.some((cf) => cf.columnName === el.name) &&
            ticket?.customFields?.[el.name])
        );
      })
    : [];
  if (!isMSP) {
    // If the user is not MSP remove the custom fields that are not visible to client
    customFields = customFields.filter((el) => el.showToClient);
    customFieldsFromForm = customFieldsFromForm.filter(
      (el) => el?.showToClient
    );
  }
  let allTechnicianFields = [];
  propertyOrder.TECHNICIAN.forEach((tech) => {
    const fieldData = allFields.find((field) => field?.name === tech);
    if (fieldData) {
      allTechnicianFields.push(fieldData);
    }
  });
  if (!isMSP) {
    if (creationForm) {
      allTechnicianFields = [];
    } else {
      allTechnicianFields.filter((el) => el.showToClient);
    }
  }
  let allTicketFields = propertyOrder.TICKET.map((el) =>
    allFields.find((prop) => prop.name === el)
  );
  if (!isMSP) {
    allTicketFields = allTicketFields.filter((el) => el.showToClient);
  }

  const allSLAFields = allFields.filter(
    (el) => propertyOrder.SLA.indexOf(el.name) > -1
  );

  let allClientFields = propertyOrder.CLIENT.map((el) =>
    allFields.find((prop) => prop.name === el)
  );
  if (!isMSP) {
    allClientFields = allClientFields.filter((el) => el.showToClient);
  }

  const updateSelection = (field, val) => {
    let serialisedValue = null;
    const updatedProperties = {};
    if (field.isCustom) {
      const allCustomFieldValues = !isEmpty(ticket.customFields)
        ? cloneDeep(ticket.customFields)
        : {};
      if (field?.nestedAttributes) {
        const nestedChild = [];
        const getNestedChilds = (fld) => {
          fld?.nestedAttributes?.map((el) => {
            const child = responseEntityFields?.getEntitiesMeta?.[0]?.attributes?.find(
              (attr) => attr?.name === el
            );
            nestedChild.push(child);
            if (child?.nestedAttributes) {
              getNestedChilds(child);
            }
          });
        };
        getNestedChilds(field);
        if (!isEmpty(nestedChild)) {
          nestedChild?.map((el) => (allCustomFieldValues[el?.name] = null));
        }
      }
      if (val?.label) {
        allCustomFieldValues[field.name] = val.label;
      } else if (field.uiDataType === 'multi_select') {
        allCustomFieldValues[field.name] = val
          ? val.map((el) => el.label)
          : null;
      } else if (field?.uiDataType === 'checkbox') {
        allCustomFieldValues[field.name] = isEmpty(val) ? null : val;
      } else {
        allCustomFieldValues[field.name] = val || null;
      }
      props.updateSelection('customFields', allCustomFieldValues);
    } else {
      switch (field.name) {
        case 'techGroup':
          serialisedValue = { groupId: val.groupId, name: val.name };
          break;
        case 'technician':
          serialisedValue = { userId: val.userId };
          break;
        case 'sop':
          serialisedValue = { sopId: val.sopId, name: val.name };
          break;
        case 'followers': {
          const parsedObject = getParsedCrudData(
            ticket.followers,
            val,
            'userId'
          );
          if (parsedObject.addedItems.length) {
            updatedProperties.addFollowers = parsedObject.addedItems.map(
              (el) => {
                return {
                  userId: el.userId
                };
              }
            );
          }
          if (parsedObject.deletedItems.length) {
            updatedProperties.deleteFollowers = parsedObject.deletedItems.map(
              (el) => {
                return {
                  userId: el.userId
                };
              }
            );
          }
          props.updateProperties(updatedProperties, props.refetchTicket);
          return;
        }
        case 'additionalRequester': {
          const requesterObject = getParsedCrudData(
            ticket.additionalRequester,
            val,
            'userId'
          );
          if (requesterObject.addedItems.length) {
            updatedProperties.addAdditionalRequester = requesterObject.addedItems.map(
              (el) => {
                return {
                  userId: el.field.userId
                };
              }
            );
          }
          if (requesterObject.deletedItems.length) {
            updatedProperties.deleteAdditionalRequester = requesterObject.deletedItems.map(
              (el) => {
                return {
                  userId: el.userId
                };
              }
            );
          }

          const callback = () => {
            props.refetchTicket();
            EventEmitter.dispatch('refreshRecipients');
          };
          props.updateProperties(updatedProperties, callback);
          return;
        }
        case 'site':
          serialisedValue = { id: val.id, name: val.name };
          break;
        default:
          serialisedValue = val.name;
      }
      props.updateSelection(field.name, serialisedValue);
    }
  };

  const handleCallback = (callback, args) => {
    if (typeof callback === 'function') {
      callback(args);
    }
    EventEmitter.dispatch('refreshRecipients');
  };

  const updateCell = (updatedObj, arg2, arg3, callback) => {
    if (props.updateProperties) {
      props.updateProperties(updatedObj, handleCallback.bind(this, callback));
    }
  };

  const getActions = (col) => {
    switch (col.name) {
      default:
        return {
          save: updateCell
        };
    }
  };

  const getCellProps = (col) => {
    return {
      type: getCellType(col),
      accessor: getAccessor(col),
      editable: creationForm ? true : isEditAuthorised && isEditable(col),
      actions: getActions(col),
      field: col,
      parentField: isDependantField(col.name)
        ? allEntityFields.find((el) => el.name === getParentField(col.name))
        : col,
      dependantField: isDependantField(col.name)
        ? col
        : allEntityFields.find((el) => el.name === getDependantField(col.name))
    };
  };

  const headerClassNames = [
    'ticket-form-header position-sticky z2 border-bottom-gray flex  actr p12 bg-white',
    !props?.isFormCollapsed && 'g10'
  ]
    .filter(Boolean)
    .join(' ');

  const fieldWrapperClassNames = ['overflow-auto ticket-form-wrapper']
    .filter(Boolean)
    .join(' ');

  const changeFormState = () => {
    if (!props?.isFormCollapsed) {
      props?.closeForm();
    }
    if (!props?.isFormCollapsed && props?.formState === FORMSTATES.DRAW) {
      props?.openForm();
    }
  };

  const shouldDisplayFormControl = props?.isFormCollapsed;

  const tooltipTitle =
    props?.formState === FORMSTATES.DRAW
      ? getTranslatedText(intl, 'ticket.details.form.open.tooltip')
      : getTranslatedText(intl, 'ticket.details.form.collapse.tooltip');

  const isSearchResultEmpty =
    !isEmpty(searchString?.trim()) &&
    isEmpty(filteredFormCustomFields) &&
    isEmpty(filteredFormTicketFields) &&
    isEmpty(filteredTicketFields) &&
    isEmpty(filterdCustomFields) &&
    isEmpty(filteredClientFields) &&
    isEmpty(filteredTechnicianFields);

  const onMouseLeave = () => {
    isTransitioning.current = true;
    props?.closeDrawer();
    setTimeout(() => {
      isTransitioning.current = false;
    }, 200);
  };

  const onMouseOver = () => {
    if (!isTransitioning.current) {
      props?.openDrawer();
    }
  };

  const isContractViewAuthorised = useIsAuthorisedHook('contract.view');

  return (
    <div
      className="ticket-form-styles full-height"
      newLayout
      onMouseLeave={onMouseLeave}
    >
      <div className={headerClassNames}>
        {shouldDisplayFormControl && (
          <div className="position-relative">
            <div
              className="pointer position-absolute ticket-form-control flex jctr actr z2"
              onClick={props?.openForm}
              onMouseOver={onMouseOver}
              id="hover-collapse closed"
            >
              <SVGIcon name="CollapseLeft" />
            </div>
          </div>
        )}
        {!shouldDisplayFormControl && (
          <>
            <div id="main-collapse form-opened">
              <Tooltip
                title={tooltipTitle}
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                className="tooltip"
                position="bottom"
              >
                <div
                  className="flex pointer bg-white br50pt"
                  onClick={changeFormState}
                >
                  <SVGIcon
                    name={
                      props?.formState === FORMSTATES.DRAW
                        ? 'CollapseLeft'
                        : 'CollapseRight'
                    }
                  />
                </div>
              </Tooltip>
            </div>

            <TextStyle weight="SemiBold" uppercase>
              <div className="fsize12">
                {getTranslatedText(intl, 'ticket.details.ticket.info')}
              </div>
            </TextStyle>
          </>
        )}
      </div>

      <div className={fieldWrapperClassNames}>
        <AccessControl
          roleToCheck="ticketContractField.view"
          productToCheck={COMMON_CONSTANTS.MSP}
          render={
            isContractViewAuthorised && (
              <TicketContract
                ticket={ticket}
                updateCell={updateCell}
                isEditable={isContractEditauthorised}
              />
            )
          }
        />

        {ticket?.sla && isMSP && (
          <div className="mb20" id="ticket-form-sla">
            <TicketSLA
              sla={ticket?.sla}
              resolutionViolated={ticket?.resolutionViolated}
              resolutionTime={ticket?.resolutionTime}
              resolutionDueTime={ticket?.resolutionDueTime}
              firstResponseViolated={ticket?.firstResponseViolated}
              firstResponseTime={ticket?.firstResponseTime}
              firstResponseDueTime={ticket?.firstResponseDueTime}
              resolutionPauseTime={ticket?.resolutionPauseTime}
            />
          </div>
        )}
        {!props?.isFormCollapsed && (
          <div className="mb20">
            <TextField
              onChange={onSearch}
              value={searchString}
              variant="outline"
              prefix={
                <div className="opacity5">
                  <SVGIcon name="Search" size="md" />
                </div>
              }
              placeholder={getTranslatedText(intl, 'ticket.icon.search')}
            />
          </div>
        )}

        {isSearchResultEmpty && (
          <div className="flex col actr jctr w100">
            <div>
              <SVGIcon name="EmptyBox" />
            </div>

            <div className="mt10">
              <TextStyle weight="Medium">
                {getTranslatedText(
                  intl,
                  'asset.details.registryEditor.noResults'
                )}
              </TextStyle>
            </div>
          </div>
        )}

        {ticket?.form?.formId && (
          <TicketFormFields
            customFields={filteredFormCustomFields || customFieldsFromForm}
            ticketFields={filteredFormTicketFields || ticketFieldsFromForm}
            ticket={ticket}
            updateCell={updateCell}
            getCellProps={getCellProps}
            showProgress={showProgress}
            inactiveCustomFields={inactiveCustomFields}
            formName={formData?.getTicketForm?.formName}
            updateSelection={updateSelection}
            formDetails={formData?.getTicketForm}
            responseEntityFields={responseEntityFields}
          />
        )}

        <TicketProperties
          allTicketFields={filteredTicketFields || allTicketFields}
          responseEntityFields={responseEntityFields}
          ticket={ticket}
          updateSelection={updateSelection}
          updateCell={updateCell}
          getCellProps={getCellProps}
          newLayout
        />

        <TechnicianFields
          allTechnicianFields={filteredTechnicianFields || allTechnicianFields}
          ticket={ticket}
          getCellProps={getCellProps}
          updateSelection={updateSelection}
          showProgress={showProgress}
          updateCell={updateCell}
          newLayout
        />

        <CustomFields
          shouldHide={
            !(
              isCustomFieldAuthorised &&
              !noCustomFields &&
              customFields.length > 0
            )
          }
          responseEntityFields={responseEntityFields}
          isEditable={isEditAuthorised || creationForm}
          customFields={filterdCustomFields || customFields}
          ticket={ticket}
          inactiveCustomFields={inactiveCustomFields}
          updateSelection={updateSelection}
          showProgress={showProgress}
          newLayout
        />

        <ClientFields
          allClientFields={filteredClientFields || allClientFields}
          ticket={ticket}
          getCellProps={getCellProps}
          updateSelection={updateSelection}
          updateCell={updateCell}
          showProgress={showProgress}
          newLayout
        />
      </div>
    </div>
  );
}

function TicketProperties({
  allTicketFields,
  ticket,
  updateSelection,
  updateCell,
  getCellProps,
  newLayout,
  hideHeading,
  responseEntityFields
}) {
  const intl = useIntl();
  const headingClassNames = newLayout
    ? 'flex actr mb-20'
    : 'flex actr mb-20 ml-5';
  const wrapperClassName = newLayout
    ? 'all-fields-wrapper flex wrap gy20 mb-20'
    : 'flex wrap mb10';

  /** Temp fix to remove the ticket Type field from the ticket properties
   * once backend migration is completed revert this code
   * TODO: TEMP FIX
   * old code: allTicketFields.filter(Boolean); // Filter undefined values
   */
  const ticketFields = allTicketFields.filter(
    (field) => field && field.name !== 'ticketType'
  );
  /** Temp fix ends here */
  if (isEmpty(ticketFields)) {
    return null;
  }

  return (
    <div className={!hideHeading ? 'mb10' : ''}>
      {!hideHeading && (
        <div className={headingClassNames}>
          <Heading element="h3" weight="Bold">
            {getTranslatedText(intl, 'ticket.details.h2.tktProperties')}
          </Heading>
        </div>
      )}

      <div className={wrapperClassName}>
        {ticketFields?.map((field) => {
          const fieldValue = ticket?.[field?.name];
          const defaultValue = getDefaultValue(fieldValue, field?.name);
          const allProps = getCellProps(field);

          return (
            <FieldComponentWrapper
              label={field?.label}
              flag={newLayout}
              dependantField={field}
              responseEntityFields={responseEntityFields}
            >
              <InlineEditTableWrapper>
                {field.entity === 'TicketStatus' ? (
                  <InlineStatusCell
                    defaultValue={defaultValue}
                    updateSelection={updateSelection}
                    _ticket={ticket}
                    field={field}
                    mode="edit"
                    params={{
                      ...allProps
                    }}
                    isClearable={!REQUIRED_TICKET_FIELDS.includes(field.name)}
                    hideTag
                  />
                ) : (
                  <InlineEdit
                    editable={allProps.editable}
                    defaultValue={defaultValue}
                    autoSave
                    showProgress
                    editView={(fieldProps) => {
                      return (
                        <>
                          <InlineSelect
                            defaultValue={defaultValue}
                            updateSelection={updateSelection}
                            _ticket={ticket}
                            field={field}
                            mode="edit"
                            updateFieldState={fieldProps.updateFieldState}
                            params={{
                              ...allProps
                            }}
                            isClearable={
                              !REQUIRED_TICKET_FIELDS.includes(field.name)
                            }
                          />
                        </>
                      );
                    }}
                    readView={() => {
                      return (
                        <InlineSelect
                          defaultValue={defaultValue}
                          updateSelection={updateSelection}
                          _ticket={ticket}
                          field={field}
                          mode="view"
                          params={{ ...allProps }}
                        />
                      );
                    }}
                    onConfirm={(value, callback) => {
                      updateCell(value, null, null, callback);
                    }}
                  />
                )}
              </InlineEditTableWrapper>
            </FieldComponentWrapper>
          );
        })}
      </div>
    </div>
  );
}

function TechnicianFields({
  allTechnicianFields,
  ticket,
  getCellProps,
  updateSelection,
  showProgress,
  updateCell,
  newLayout
}) {
  const intl = useIntl();
  const headingClassNames = newLayout
    ? 'flex actr mb-20'
    : 'flex actr mb-20 ml-5';
  const wrapperClassName = newLayout
    ? 'all-fields-wrapper flex wrap gy20 mb-20'
    : 'flex wrap mb10';

  if (!allTechnicianFields.length) return null;
  return (
    <div className="mb10 ">
      <div className={headingClassNames}>
        <Heading element="h3" weight="Bold">
          {getTranslatedText(intl, 'ticket.details.h2.techProperties')}
        </Heading>
      </div>
      <div className={wrapperClassName}>
        {allTechnicianFields?.map((field) => {
          const fieldValue = ticket?.[field?.name];
          const defaultValue = getDefaultValue(fieldValue, field?.name);
          const allProps = getCellProps(field);
          const Tag = CELL_TYPES[allProps.type];
          return (
            <FieldComponentWrapper label={field?.label} flag={newLayout}>
              {allProps.type === 'MultiSelectText' ? (
                <Tag
                  defaultValue={defaultValue}
                  updateSelection={updateSelection}
                  _ticket={ticket}
                  field={field}
                  params={allProps}
                  showProgress={showProgress}
                />
              ) : (
                <InlineEditTableWrapper>
                  <InlineEdit
                    editable={allProps.editable}
                    defaultValue={defaultValue}
                    autoSave
                    showProgress
                    editView={(fieldProps) => {
                      return (
                        <>
                          <InlineDependantSelect
                            defaultValue={defaultValue}
                            updateSelection={updateSelection}
                            _ticket={ticket}
                            field={field}
                            mode="edit"
                            updateFieldState={fieldProps.updateFieldState}
                            params={{
                              ...allProps
                            }}
                            isClearable={
                              !REQUIRED_TICKET_FIELDS.includes(field.name)
                            }
                          />
                        </>
                      );
                    }}
                    readView={() => {
                      return (
                        <InlineDependantSelect
                          defaultValue={defaultValue}
                          updateSelection={updateSelection}
                          _ticket={ticket}
                          field={field}
                          mode="view"
                          params={{ ...allProps }}
                        />
                      );
                    }}
                    onConfirm={(value, callback) => {
                      updateCell(value, null, null, callback);
                    }}
                  />
                </InlineEditTableWrapper>
              )}
            </FieldComponentWrapper>
          );
        })}
      </div>
    </div>
  );
}

function CustomFields({
  shouldHide,
  customFields,
  ticket,
  inactiveCustomFields,
  updateSelection,
  showProgress,
  isEditable: _isEditable,
  newLayout,
  hideHeading,
  responseEntityFields
}) {
  const intl = useIntl();

  const CustomFields = customFields.filter(Boolean); // Filter undefined values

  const getCustomTag = (field) => {
    switch (field.uiDataType) {
      case 'paragraph':
      case 'text':
      case 'numeric':
      case 'decimal':
        return CustomTextField;
      case 'date':
      case 'datetime':
        return CustomDateField;
      case 'radio':
        return AsyncRadioV2;
      case 'checkbox':
        return AsyncCheckboxV2;
      case 'select':
      case 'multi_select':
        return AsyncSelectV2;
      case 'secure_text':
        return SecureText;
      default:
        return EditableField;
    }
  };
  if (shouldHide || !CustomFields?.length) {
    return null;
  }
  const headingClassNames = newLayout
    ? 'flex actr mb-20'
    : 'flex actr mb-20 ml-5';
  const wrapperClassName = newLayout
    ? 'all-fields-wrapper flex wrap gy20 mb-20 custom-fields'
    : 'flex wrap mb10';

  return (
    <div className={!hideHeading ? 'mb10' : ''}>
      {!hideHeading && (
        <div className={headingClassNames}>
          <Heading element="h3" weight="Bold">
            {getTranslatedText(intl, 'ticket.details.h2.otherProp')}
          </Heading>
        </div>
      )}

      <div className={wrapperClassName}>
        {CustomFields.map((field) => {
          let fieldValue = ticket?.customFields?.[field.name] || '';
          if (fieldValue && field.uiDataType === 'select') {
            fieldValue = {
              name: fieldValue,
              value: fieldValue,
              label: fieldValue
            };
          } else if (fieldValue && field.uiDataType === 'multi_select') {
            fieldValue = fieldValue.map((el) => {
              return {
                name: el,
                value: el,
                label: el
              };
            });
          }
          const Tag = getCustomTag(field);

          let parentValueExist = true;

          const parentField =
            (field?.nestedTo &&
              responseEntityFields?.getEntitiesMeta?.[0]?.attributes?.find(
                (el) => el?.name === field?.nestedTo
              )) ||
            null;

          if (field?.isDependent) {
            const parentValue =
              getCellValue(
                ticket,
                parentField?.isCustom
                  ? `customFields.${field?.nestedTo}`
                  : field?.nestedTo
              ) || null;
            if (!parentValue) {
              parentValueExist = false;
            }
          }
          let isDisabled = false;
          // Check if custom field is inactive
          const isCustomFieldInactive = inactiveCustomFields.some(
            (cf) => cf.columnName === field.name
          );
          // Check if the custom field is dependant
          const isDependent = field?.isDependent;

          if (isCustomFieldInactive) {
            // If custom field is inactive set disabled to true
            isDisabled = true;
          } else if (isDependent) {
            // If the custom field is dependant set isDisabled based on parent value
            isDisabled = !parentValueExist;
          }

          // Disable editing for inactive custom field that has value
          return (
            <FieldComponentWrapper
              label={field.label}
              flag={newLayout}
              classNames={`ticket-custom-fields ${newLayout ? 'w100' : ''} `}
              dependantField={field}
              responseEntityFields={responseEntityFields}
            >
              <Tag
                defaultValue={fieldValue}
                updateSelection={(v) => {
                  updateSelection(field, v);
                }}
                value={fieldValue}
                ticket={ticket}
                entityId={ticket?.ticketId}
                field={field}
                editable={_isEditable && !isDisabled}
                disableSelection={!parentValueExist}
                inlineEdit
                module={ALL_MODULES.ticket}
                isMulti={field.uiDataType === 'multi_select'}
                showProgress={showProgress}
                parentField={parentField}
              />
            </FieldComponentWrapper>
          );
        })}
      </div>
    </div>
  );
}

function ClientFields({
  allClientFields,
  ticket,
  getCellProps,
  updateSelection,
  updateCell,
  showProgress,
  newLayout
}) {
  const intl = useIntl();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const wrapperClassName = newLayout
    ? 'all-fields-wrapper flex wrap gy20 mb-20 full-height w100'
    : ' flex wrap mb10';
  const headingClassNames = newLayout
    ? 'flex actr mb-20'
    : 'flex actr mb-20 ml-5';

  function render(field) {
    const fieldValue = ticket[field.name];
    const defaultValue = getDefaultValue(fieldValue, field?.name);
    const allProps = getCellProps(field);
    const Tag = CELL_TYPES[allProps.type];

    if (includes(['AsyncMultiSelectUser', 'Text'], allProps.type)) {
      if (field.name === 'site') {
        return (
          <InlineEditTableWrapper>
            <InlineEdit
              editable={allProps.editable}
              defaultValue={defaultValue}
              autoSave
              showProgress
              editView={(fieldProps) => {
                return (
                  <InlineSelect
                    defaultValue={defaultValue}
                    updateSelection={updateSelection}
                    _ticket={ticket}
                    field={field}
                    mode="edit"
                    updateFieldState={fieldProps.updateFieldState}
                    params={allProps}
                  />
                );
              }}
              readView={() => {
                return (
                  <InlineSelect
                    defaultValue={defaultValue}
                    updateSelection={updateSelection}
                    _ticket={ticket}
                    field={field}
                    mode="view"
                    params={allProps}
                  />
                );
              }}
              onConfirm={(value, callback) => {
                updateCell(value, null, null, callback);
              }}
            />
          </InlineEditTableWrapper>
        );
      }

      return (
        <div
          className={`${
            field.name === 'additionalRequester'
              ? 'additional-requester-field'
              : ''
          }`}
        >
          <Tag
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            _ticket={ticket}
            field={field}
            params={allProps}
            client={ticket?.client}
            showProgress={showProgress}
          />
        </div>
      );
    }

    return (
      <InlineEditTableWrapper>
        <InlineEdit
          editable={allProps.editable}
          defaultValue={defaultValue}
          autoSave
          showProgress
          editView={(fieldProps) => {
            return (
              <InlineDependantSelect
                defaultValue={defaultValue}
                updateSelection={updateSelection}
                _ticket={ticket}
                field={field}
                mode="edit"
                updateFieldState={fieldProps.updateFieldState}
                params={allProps}
                isClearable={!REQUIRED_TICKET_FIELDS.includes(field.name)}
              />
            );
          }}
          readView={() => {
            return (
              <InlineDependantSelect
                defaultValue={defaultValue}
                updateSelection={updateSelection}
                _ticket={ticket}
                field={field}
                mode="view"
                params={allProps}
              />
            );
          }}
          onConfirm={(value, callback) => {
            updateCell(value, null, null, callback);
          }}
        />
      </InlineEditTableWrapper>
    );
  }

  if (!allClientFields.length) return null;

  return (
    <div className="mb10">
      <AccessControl
        productToCheck={COMMON_CONSTANTS.MSP}
        render={
          <div className={headingClassNames}>
            <Heading element="h3" weight="Bold">
              {getTranslatedText(intl, 'ticket.details.h2.ClientProperties')}
            </Heading>
          </div>
        }
      />
      <div className={wrapperClassName}>
        {allClientFields.map((field) => {
          if (!isMSPProduct && field.name === 'client') {
            return <></>;
          }
          return (
            <FieldComponentWrapper label={field.label} flag={newLayout}>
              {render(field)}
            </FieldComponentWrapper>
          );
        })}
      </div>
    </div>
  );
}

function TicketFormFields(props) {
  const {
    ticketFields,
    customFields,
    ticket,
    updateSelection,
    updateCell,
    getCellProps,
    showProgress,
    inactiveCustomFields,
    formName,
    formDetails,
    responseEntityFields
  } = props;
  const isEditAuthorised = useIsAuthorisedHook('tickets.edit');
  // Reorder form fields from left to right from template layout
  /**
  console.log(
    formDetails,
    'formDetails',
    JSON.parse(formDetails?.fieldDetails)
  );
   */

  if (!isEmpty(ticketFields) || !isEmpty(customFields)) {
    return (
      <>
        <div className="flex actr mb-20">
          <Heading element="h3" weight="Bold">
            {formName || 'NA'}
          </Heading>
        </div>

        {!isEmpty(ticketFields) && (
          <TicketProperties
            allTicketFields={ticketFields || []}
            ticket={ticket}
            updateSelection={updateSelection}
            updateCell={updateCell}
            getCellProps={getCellProps}
            newLayout
            hideHeading
            responseEntityFields={responseEntityFields}
          />
        )}

        {!isEmpty(customFields) && (
          <CustomFields
            shouldHide={customFields?.length === 0}
            isEditable={isEditAuthorised}
            customFields={customFields}
            ticket={ticket}
            inactiveCustomFields={inactiveCustomFields || []}
            updateSelection={updateSelection}
            showProgress={showProgress}
            newLayout
            hideHeading
            responseEntityFields={responseEntityFields}
          />
        )}
      </>
    );
  }

  return <></>;
}

function getLabel(contract) {
  return {
    name: contract?.name ?? contract?.billableContract?.chargeItem?.name,
    label: contract?.name ?? contract?.billableContract?.chargeItem?.name,
    value: contract?.contractId
  };
}

function TicketContract({ ticket, updateCell, isEditable }) {
  const contractTicketMapping = ticket?.contractTicketMapping;

  const isItProduct = useCheckIsItProduct();

  const intl = useIntl();

  const [options, setOptions] = useState([]);

  const defaultSelectedContract = contractTicketMapping?.find(
    (ticketMap) => ticketMap.inUse
  )?.contract;

  const defaultValue = getLabel(defaultSelectedContract);

  const displayVal = defaultValue?.name ?? '';

  const { loading: fetchContractsLoading } = useQuery(
    FETCH_CLIENT_CONTRACTS_LIST_FOR_TICKET,
    {
      client: AccountClient,
      fetchPolicy: 'no-cache',
      onCompleted: (resp) => {
        if (resp?.fetchClientContractList) {
          return setOptions([
            {
              label: getTranslatedText(intl, 'msp.contract.matchingContract'),
              options: (contractTicketMapping ?? []).map((t) => {
                return getLabel(t?.contract);
              })
            },
            {
              label: getTranslatedText(intl, 'msp.contract.otherContracts'),
              options: (
                resp?.fetchClientContractList?.clientContracts ?? []
              ).map((c) => {
                return getLabel(c?.contract);
              })
            }
          ]);
        }
        setOptions([]);
      },
      variables: {
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'client.accountId',
                operator: 'is',
                value: ticket?.client?.accountId
              },
              {
                joinOperator: 'OR',
                operands: [
                  {
                    attribute:
                      'contract.billableContract.contractSiteMapping.siteId',
                    operator: 'isEmpty'
                  },
                  {
                    attribute:
                      'contract.billableContract.contractSiteMapping.siteId',
                    operator: 'is',
                    value: ticket?.site?.id
                  }
                ]
              },
              {
                attribute: 'contract.contractId',
                operator: 'notIncludes',
                value: contractTicketMapping?.map(
                  (contract) => contract?.contractId
                )
              },
              {
                joinOperator: 'OR',
                operands: [
                  {
                    joinOperator: 'AND',
                    operands: [
                      {
                        attribute: 'startDate',
                        operator: 'lessThanOrEqual',
                        value: convertToMSPTimeZone(null, true)
                      },
                      {
                        attribute: 'contract.contractType',
                        operator: 'includes',
                        value: ['SERVICE', 'TIME_AND_MATERIAL']
                      },
                      {
                        attribute:
                          'contract.billableContract.chargeItem.serviceTypeItem.offeringType',
                        operator: 'notIncludes',
                        value: ['BLOCK_HOUR', 'BLOCK_MONEY']
                      }
                    ]
                  },
                  {
                    joinOperator: 'AND',
                    operands: [
                      {
                        attribute: 'contractStatus',
                        operator: 'is',
                        value: 'ACTIVE'
                      },
                      {
                        attribute:
                          'contract.billableContract.chargeItem.serviceTypeItem.offeringType',
                        operator: 'includes',
                        value: ['BLOCK_HOUR', 'BLOCK_MONEY']
                      }
                    ]
                  }
                ]
              },
              {
                attribute: 'contractStatus',
                operator: 'isNot',
                value: 'INACTIVE'
              }
            ]
          }
        }
      }
    }
  );

  if (isItProduct) return null;

  return (
    <div className="mb-5">
      <FieldComponentWrapper
        fullWidth
        label={<FormattedMessage id="common.contract" />}
        flag
      >
        <InlineEditTableWrapper>
          <InlineEdit
            editable={isEditable}
            defaultValue={defaultValue}
            autoSave
            showProgress
            editView={(fieldProps) => {
              return (
                <SuperSelect
                  value={defaultValue}
                  onChange={(item) => {
                    fieldProps.updateFieldState(item);
                  }}
                  options={options}
                  isDisabled={false}
                  width={null}
                  autoFocus
                  defaultMenuIsOpen
                  noOptionsMessage={() => {
                    return fetchContractsLoading ? 'Loading...' : 'No options';
                  }}
                />
              );
            }}
            readView={() => {
              return (
                <Wrap
                  className={`${
                    isEditable ? 'editable-cell' : 'cell'
                  } flex center full-height inlineSelect`}
                >
                  <div className="mr-10">
                    <SVGIcon name="Contract" />
                  </div>
                  <WrapperTag
                    className="ellipsis self-center flex center"
                    title={displayVal}
                  >
                    {isEditable
                      ? displayVal || (
                          <DisabledTag>
                            <FormattedMessage id="msp.contract.chooseContract" />
                          </DisabledTag>
                        )
                      : displayVal || 'NA'}
                  </WrapperTag>
                  {isEditable && (
                    <div className="edit-actions">
                      <div className="ml8 fx center">
                        <SVGIcon name="Edit" />
                      </div>
                    </div>
                  )}
                </Wrap>
              );
            }}
            onConfirm={(value, callback) => {
              let input = null;
              if (value) {
                input = {
                  contract: {
                    contractId: value.value
                  }
                };
              }
              updateCell(input, null, null, callback);
            }}
          />
        </InlineEditTableWrapper>
      </FieldComponentWrapper>
    </div>
  );
}

export default injectIntl(withErrorBoundary(TicketDetailForm));
